import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useMediaQuery } from "@mui/material";
import { DatePicker, DatePickerSlotProps } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { useField } from "formik";
import { merge } from "lodash";
import React from "react";

import CalendarIcon from "web/assets/icons/cafe-calendar.svg";

import getDatepickerSlotProps from "./getDatepickerSlotProps";

interface DatepickerFormikMUIProps {
  placeholder?: string;
  name: string;
  minDate?: Dayjs | number | string | null;
  maxDate?: Dayjs | number | string | null;
  disabled?: boolean;
  dataT1?: string;
  id?: string;
  slotProps?: DatePickerSlotProps<dayjs.Dayjs, false>;
  isError?: boolean;
  hasExternalErrorComponent?: boolean;
}

dayjs.extend(utc);

const DatepickerFormikMUI: React.FC<DatepickerFormikMUIProps> = ({
  placeholder = "Data",
  name,
  minDate = null,
  maxDate = null,
  disabled = false,
  dataT1 = "date_picker_formik",
  id,
  slotProps,
  isError: isErrorProps,
  hasExternalErrorComponent = false,
}) => {
  const [field, meta, helpers] = useField(name);
  const isError = isErrorProps || Boolean(meta.touched && meta.error);
  const { setValue } = helpers;

  const rawValue = field.value;

  const currentValue =
    typeof rawValue === "number" && Number.isFinite(rawValue)
      ? dayjs.utc(rawValue)
      : null;

  const handleChange = (newValue: Dayjs | null) => {
    if (newValue) {
      const utcMidnightTimestamp = Date.UTC(
        newValue.year(),
        newValue.month(),
        newValue.date()
      );
      setValue(utcMidnightTimestamp);
    } else {
      setValue(null);
    }
  };

  const isMobile = useMediaQuery("(max-width: 979px)");

  const minDateFormatted = minDate
    ? dayjs(minDate === "today" ? undefined : minDate)
    : undefined;

  return (
    <DatePicker
      desktopModeMediaQuery="(min-width:980px)"
      value={currentValue}
      views={["year", "month", "day"]}
      openTo="day"
      onChange={handleChange}
      disabled={disabled}
      minDate={minDateFormatted}
      maxDate={maxDate ? dayjs(maxDate) : undefined}
      format="DD/MM/YYYY"
      slotProps={merge(
        getDatepickerSlotProps({
          id,
          isError,
          errorText: hasExternalErrorComponent ? "" : meta.error,
          placeholder,
          name,
          dataT1,
          disabled,
          isMobile,
        }),
        slotProps
      )}
      slots={{
        openPickerIcon: CalendarIcon,
        switchViewIcon: KeyboardArrowDownIcon,
      }}
    />
  );
};

export default DatepickerFormikMUI;
